// Task types: the shape of the work an agent job carries (ai_jobs.task_type). The queue mechanics (claim, spec,
// build, approval, submit) are the same for every type; what changes is what the spec holds and what the human
// edits before approving. Agents → Task types shows each one with a sample job at every stage.
//
// Add a type here, in the ai_jobs_task_type_check constraint, and (when its spec has new fields) in the cockpit-cli
// summary validator, then give it a fixture in src/components/ai/operator/fixtures.

import type { TaskTypeId } from './supabase.types';

export type { TaskTypeId };

export interface TaskType {
  id: TaskTypeId;
  label: string;
  /** One line for lists. */
  blurb: string;
  /** What the spec holds and what the human does with it. */
  detail: string[];
  /** The stages a job of this type moves through, in order. */
  stages: string[];
}

export const TASK_TYPES: TaskType[] = [
  {
    id: 'general',
    label: 'General Fulfillment',
    blurb: 'The workhorse: any client request, from a one-line fix to a feature.',
    detail: [
      'Spec: the requests in the client’s words, one proposed action each, decisions to answer, need-to-know, repo and database.',
      'Build: a check per request (before, after, how to check it), the PR and the preview.',
      'Approval: what’s live per request and the paste-ready client note.',
    ],
    stages: ['Spec', 'Approve spec', 'Build', 'Approve build', 'Submit'],
  },
  {
    id: 'page_build',
    label: 'Page Build',
    blurb: 'One page built from scratch to persuade one buyer into one action, section by section, each section by its own agent.',
    detail: [
      'Spec starts with the buyer: who searches this, what they want in their words, what they already know, what holds them back, the page’s one promise and the one action it asks for.',
      'Then the target search (given with the job, or picked from Search Console and Ahrefs volume with the reason) and the sections, as direction rather than copy, ordered to persuade the buyer into the action. Source material goes in its section as given; a few sections get a generated image; one or two are signature moments.',
      'Edit the buyer’s keyword or any section before approving, the same way you edit the client note. The build follows what you saved; "Agent’s version" puts the spec back.',
      'Build: one section at a time, in page order, each by a fresh agent with the Page Build section prompt (Agents → Prompts): clear, restrained and in the buyer’s words, with the punch saved for the signature moments.',
      'Buyer test once every section is in: a fresh agent reads the preview cold as the buyer and fails anything that does not say what it is, who it is for and what to do next within five seconds.',
      'SEO review after that: keyword density 0.5–1%, title and meta, headings, alt text and image file names, structured data checked against what the page and the business can back up, links, mobile PageSpeed. Any fail goes back to the section that caused it.',
      'Approval: the page checked live, plus the client note.',
    ],
    stages: ['Spec (buyer + keyword + sections)', 'Edit + approve', 'Build, one section at a time', 'Buyer test', 'SEO review', 'Approve build', 'Submit'],
  },
  {
    id: 'security_audit',
    label: 'Security Audit',
    blurb: 'One system (a database and every repo and site that uses it) checked for holes a stranger can exploit, and for what could lose data.',
    detail: [
      'Started from Security → Audits, one open audit per system (different systems run side by side); auto-approve never applies. The agent follows the workspace skill security-audit and the editable Security Audit prompt (Agents → Prompts), where repeat lessons become checklist lines.',
      'Spec is the analysis: Security (codebase, database, live config) and Integrity (cascading deletes, migration drift, backups). Only proven findings count: the exact request that worked, as a stranger where possible, and what came back.',
      'Each finding says what a stranger can do, the proof, what the fix changes and what it could break, and you answer it Fix / Skip / Later on its row. Minor or unlikely things are one parked line, never a decision.',
      'Every cascading foreign key is labelled good or bad with a one-line why and a BEGIN … ROLLBACK count of what one real delete takes. Your Change it / Good cascade, keep is saved for the system, so the next audit never asks about that link again.',
      'Build fixes only what you marked Fix. Database fixes go live once they pass their own checks (rollback written first, dry run as each role, before/after diff); code fixes are PRs you merge.',
      'Verification: a snapshot of public pages and every app query as each role, before and after (any diff is rolled back), role walkthroughs with throwaway users deleted the same run, and a list of anything not tested because it sends email.',
    ],
    stages: ['Spec (analysis)', 'Approve findings', 'Build (fixes)', 'Approve build', 'Submit'],
  },
];

export const taskTypeOf = (id: string | null | undefined): TaskType => TASK_TYPES.find((t) => t.id === id) ?? TASK_TYPES[0];
